import { useState, useEffect, useRef } from 'react';
import '@/App.css';
import './i18n';  // V1.3 : Initialise l'internationalisation
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

// Pages
import LandingPage from './pages/LandingPage';
import AuditPackPage from './pages/AuditPackPage';
import NotFoundPage from './pages/NotFoundPage';
import MaintenancePage from './pages/MaintenancePage';
import Subprocessors from './pages/Subprocessors';
import HelpCenter from './pages/HelpCenter';
import VerifyEmailPage from './pages/VerifyEmailPage';
import CookieConsent from './components/layout/CookieConsent';
import MaintenanceGate from './components/layout/MaintenanceGate';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import MentionsLegales from './pages/MentionsLegales';
import CharteConfidentialite from './pages/CharteConfidentialite';
import CGV from './pages/CGV';
import { BlogList, BlogArticle } from './pages/BlogPage';
import AuthCallback from './pages/AuthCallback';
// V1.3 : AdminPortalPage désormais inutilisé (routes redirigent vers /admin).
// Fichier conservé dans le repo pour historique, import retiré.
import AdminDashboard from './pages/AdminDashboard';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import MissionPortalPage from './pages/MissionPortalPage';
import MissionManagementView from './pages/missions/MissionManagementView';
import MissionManagePage from './pages/MissionManagePage';
// V1.3 : ChoixMissionPage legacy retiré — /choix-mission redirige vers /dashboard?tab=payment
// V5.44 : Page admin détails mission
import AdminMissionDetailsPage from './pages/AdminMissionDetailsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    checkAuth();
  }, []);
  
  return { user, setUser, loading, checkAuth };
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      const token = localStorage.getItem('token');

      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.data) {
          setUser(response.data);
          setIsAuthenticated(true);
        }
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.removeItem('token');
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-cyan-400 font-mono text-lg">
          <span className="glitch-subtle">AUTHENTIFICATION_EN_COURS...</span>
        </div>
      </div>
    );
  }

  return children;
};

// V1.2 — AdminProtectedRoute : bloque l'accès aux pages admin si non connecté OU non-admin
const AdminProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // 'checking' | 'ok' | 'denied'

  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatus('denied');
        navigate('/login', { replace: true });
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // V1.2 FIX : /auth/me renvoie { user: { email, isAdmin, role, ... } }
        // Support les deux formats : res.data.user.xxx ou res.data.xxx directement
        const payload = res.data?.user || res.data || {};
        const userEmail = (payload.email || '').toLowerCase();
        const isAdmin =
          payload.isAdmin === true ||
          payload.is_admin === true ||
          payload.role === 'admin' ||
          userEmail === 'leffaceur@effaceurpro.fr';
        if (isAdmin) {
          setStatus('ok');
        } else {
          setStatus('denied');
          // Non-admin connecté : retour au dashboard client
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        setStatus('denied');
        localStorage.removeItem('token');
        navigate('/login', { replace: true });
      }
    };
    checkAdmin();
  }, [navigate]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-cyan-400 font-mono text-lg">
          <span className="glitch-subtle">VÉRIFICATION_ADMIN...</span>
        </div>
      </div>
    );
  }
  if (status === 'denied') {
    return null; // navigate a déjà redirigé
  }
  return children;
};

function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment (Google Auth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <MaintenanceGate>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ForgotPasswordPage />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/confidentialite" element={<CharteConfidentialite />} />
      <Route path="/cgv" element={<CGV />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/audit" element={<ProtectedRoute><AuditPackPage /></ProtectedRoute>} />
      <Route path="/audit-pouvoir-achat" element={<ProtectedRoute><AuditPackPage /></ProtectedRoute>} />
      <Route path="/subprocessors" element={<Subprocessors />} />
      <Route path="/sous-traitants" element={<Subprocessors />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/aide" element={<HelpCenter />} />
      <Route path="/faq" element={<HelpCenter />} />
      {/* V1.2 — Routes admin sécurisées (vérification is_admin côté frontend) */}
      <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
      {/* V1.3 : /admin-portal-effaceur et /admin-dashboard → alias vers /admin (fusion) */}
      <Route path="/admin-portal-effaceur" element={<Navigate to="/admin" replace />} />
      <Route path="/admin-dashboard" element={<Navigate to="/admin" replace />} />
      {/* V1.3 : /choix-mission et /packs redirigent vers /dashboard?tab=payment
          (seule page légitime de choix de pack, style cyber + CGV) */}
      <Route path="/choix-mission" element={<Navigate to="/dashboard?tab=payment" replace />} />
      <Route path="/packs" element={<Navigate to="/dashboard?tab=payment" replace />} />
      {/* Obfuscated admin routes → alias vers /admin */}
      <Route path="/ctrl-x7k9m2p4" element={<Navigate to="/admin" replace />} />
      <Route path="/gest-r8n5v3q1" element={<Navigate to="/admin" replace />} />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/*" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      {/* Mission Portal - Phase C : Collecte intelligente de documents */}
      <Route path="/mission-portal/:type" element={
        <ProtectedRoute>
          <MissionPortalPage />
        </ProtectedRoute>
      } />
      {/* V5.3 REFONDATION : Routes Silos isolés */}
      <Route path="/mission/:type" element={
        <ProtectedRoute>
          <MissionPortalPage />
        </ProtectedRoute>
      } />
      <Route path="/missions/demenagement" element={
        <ProtectedRoute>
          <MissionPortalPage />
        </ProtectedRoute>
      } />
      <Route path="/missions/succession" element={
        <ProtectedRoute>
          <MissionPortalPage />
        </ProtectedRoute>
      } />
      <Route path="/missions/audit" element={
        <ProtectedRoute>
          <MissionPortalPage />
        </ProtectedRoute>
      } />
      {/* V5.7 REFONDATION : Route vers la vue de gestion post-signature */}
      <Route path="/missions/manage/:missionId" element={
        <ProtectedRoute>
          <MissionManagementView />
        </ProtectedRoute>
      } />
      {/* V5.33 : Route "Gérer mon dossier" avec query param */}
      <Route path="/missions/manage" element={
        <ProtectedRoute>
          <MissionManagePage />
        </ProtectedRoute>
      } />
      {/* V5.44 : Route Admin détails mission (protégée) */}
      <Route path="/admin/missions/:missionId" element={<AdminProtectedRoute><AdminMissionDetailsPage /></AdminProtectedRoute>} />
      {/* V1.4 : Maintenance + 404 catch-all */}
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </MaintenanceGate>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-void">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0A0F1F',
            border: '1px solid #00F5FF',
            color: '#E0E0E0',
            fontFamily: 'Space Mono, monospace'
          }
        }}
      />
      <BrowserRouter>
        <AppRouter />
        <CookieConsent />
      </BrowserRouter>
    </div>
  );
}

export default App;
